/**
 * Execution Service
 *
 * API service for agent execution management.
 */
import api from './api';
import {
  Execution,
  ExecuteAgentRequest,
  ExecuteAgentResponse,
  ExecutionListResponse,
  ExecutionListParams,
  ExecutionStats,
} from '../types/execution';

export const executionService = {
  /**
   * Execute an agent
   *
   * @param agentId - The agent ID to execute
   * @param data - Execution request data (input and priority)
   * @returns ExecuteAgentResponse with execution details
   */
  async execute(agentId: string, data?: ExecuteAgentRequest): Promise<ExecuteAgentResponse> {
    const response = await api.post<ExecuteAgentResponse>(
      `/agents/${agentId}/execute`,
      data || {}
    );
    return response.data;
  },

  /**
   * Get a single execution by ID
   *
   * @param executionId - The execution ID to fetch
   * @returns Execution details
   */
  async get(executionId: string): Promise<Execution> {
    const response = await api.get<Execution>(`/executions/${executionId}`);
    return response.data;
  },

  /**
   * List executions with pagination and filters
   *
   * @param params - Query parameters for filtering and pagination
   * @returns Paginated list of executions
   */
  async list(params?: ExecutionListParams): Promise<ExecutionListResponse> {
    const response = await api.get<ExecutionListResponse>('/executions', {
      params,
    });
    return response.data;
  },

  /**
   * List executions for a specific agent
   *
   * @param agentId - The agent ID to filter by
   * @param params - Additional query parameters
   * @returns Paginated list of executions for the agent
   */
  async listByAgent(
    agentId: string,
    params?: Omit<ExecutionListParams, 'agentId'>
  ): Promise<ExecutionListResponse> {
    const response = await api.get<ExecutionListResponse>('/executions', {
      params: { ...params, agent_id: agentId },
    });
    return response.data;
  },

  /**
   * Cancel a running execution
   *
   * @param executionId - The execution ID to cancel
   * @returns Updated execution with cancelled status
   */
  async cancel(executionId: string): Promise<Execution> {
    const response = await api.post<Execution>(`/executions/${executionId}/cancel`);
    return response.data;
  },

  /**
   * Replay an execution — re-run the same agent with the same input.
   *
   * @param executionId - The past execution to re-run
   * @returns The new PENDING execution
   */
  async replay(executionId: string): Promise<Execution> {
    const response = await api.post<Execution>(`/executions/${executionId}/replay`);
    return response.data;
  },

  /**
   * Get execution statistics
   *
   * @param agentId - Optional agent ID to filter stats
   * @returns Execution statistics
   */
  async getStats(agentId?: string): Promise<ExecutionStats> {
    const response = await api.get<ExecutionStats>('/executions/stats', {
      params: agentId ? { agentId } : undefined,
    });
    return response.data;
  },
};

export default executionService;
